// Dialog „Erinnere mich" (Nachrichtenmenü) bzw. „Neue Erinnerung" ohne Nachricht (Seite /reminders). Vertrag: 3.10.
import { FIXED_LIMITS } from '@lagerfeuer/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { remindersApi } from '../api';
import { formatDateTime, formatRelative, fromLocalInput, toLocalInput } from '../format';
import type { ExtraContext } from '../index';
import { useStableHandler } from '../scheduled/hooks';
import { atDayTime, inMinutes, limitAwareMessage, nextWeekday, validateRemindAt } from '../scheduled/time';
import { toast } from '../toast';
import { Button, Dialog, Field } from '../ui';
import { REMINDERS_KEY } from './notify';
import './reminders.css';

type PresetId = '20m' | '1h' | '3h' | 'tomorrow' | 'next-week' | 'custom';
const PRESETS: { id: PresetId; label: string; at?: (now: number) => Date }[] = [
  { id: '20m', label: 'In 20 Minuten', at: (now) => inMinutes(20, now) },
  { id: '1h', label: 'In 1 Stunde', at: (now) => inMinutes(60, now) },
  { id: '3h', label: 'In 3 Stunden', at: (now) => inMinutes(180, now) },
  { id: 'tomorrow', label: 'Morgen 9:00', at: (now) => atDayTime(1, 9, 0, now) },
  { id: 'next-week', label: 'Nächste Woche (Mo 9:00)', at: (now) => nextWeekday(1, 9, 0, now) },
  { id: 'custom', label: 'Eigener Zeitpunkt' },
];

export function RemindMeDialog({ messageId, onClose: onCloseProp }: ExtraContext) {
  const onClose = useStableHandler(onCloseProp);
  const qc = useQueryClient();
  const noteMax = FIXED_LIMITS.reminderNoteMax;
  const [openedAt] = useState(() => Date.now());
  const [preset, setPreset] = useState<PresetId>('1h');
  const [custom, setCustom] = useState(() => toLocalInput(inMinutes(60, openedAt)));
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (body: { remindAt: string; note?: string; messageId?: string }) => remindersApi.create(body),
    onSuccess: (dto) => {
      void qc.invalidateQueries({ queryKey: REMINDERS_KEY });
      toast({ kind: 'success', title: 'Erinnerung gespeichert', body: `${formatDateTime(dto.remindAt)} (${formatRelative(dto.remindAt)})`, href: '/reminders' });
      onClose();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const now = Date.now();
    const p = PRESETS.find((x) => x.id === preset);
    const remindAt = p?.at ? p.at(now).toISOString() : fromLocalInput(custom);
    const err = validateRemindAt(remindAt, now) ?? (note.length > noteMax ? `Notiz: höchstens ${noteMax} Zeichen.` : null);
    setError(err);
    if (err || !remindAt) return;
    const trimmed = note.trim();
    create.mutate({ remindAt, ...(trimmed ? { note: trimmed } : {}), ...(messageId ? { messageId } : {}) });
  };

  const presetPreview = (p: (typeof PRESETS)[number]) => (p.at ? formatDateTime(p.at(openedAt).toISOString()) : null);

  return (
    <Dialog
      title={messageId ? 'Erinnere mich' : 'Neue Erinnerung'}
      testId="reminders-dialog"
      onClose={onClose}
      footer={
        <>
          <Button testId="reminders-dialog-cancel" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" type="submit" form="lfx-remind-form" testId="reminders-dialog-submit" disabled={create.isPending}>
            {create.isPending ? 'Speichere …' : 'Erinnern'}
          </Button>
        </>
      }
    >
      <form id="lfx-remind-form" className="lfx-stack" onSubmit={submit} noValidate>
        {messageId && <p className="lfx-muted lfx-remind-intro">Du wirst zum gewählten Zeitpunkt an diese Nachricht erinnert.</p>}
        <div className="lfx-remind-presets" role="radiogroup" aria-label="Zeitpunkt">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={preset === p.id}
              className={`lfx-remind-preset${preset === p.id ? ' lfx-remind-preset--active' : ''}`}
              data-testid={`reminders-dialog-preset-${p.id}`}
              onClick={() => setPreset(p.id)}
            >
              <span>{p.label}</span>
              {presetPreview(p) && <span className="lfx-muted lfx-remind-preset__at">{presetPreview(p)}</span>}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <Field label="Eigener Zeitpunkt">
            {(id) => <input id={id} type="datetime-local" className="lfx-input" data-testid="reminders-dialog-when" value={custom} onChange={(e) => setCustom(e.target.value)} />}
          </Field>
        )}
        <Field
          label="Notiz (optional)"
          hint={
            <span className={note.length > noteMax ? 'lfx-remind-count lfx-remind-count--over' : 'lfx-remind-count'} data-testid="reminders-dialog-count">
              {note.length} / {noteMax}
            </span>
          }
        >
          {(id) => (
            <textarea id={id} className="lfx-textarea" data-testid="reminders-dialog-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Woran soll ich dich erinnern?" />
          )}
        </Field>
        {error && (
          <div className="lfx-field__error" role="alert" data-testid="reminders-dialog-validation">
            {error}
          </div>
        )}
        {create.error && (
          <div className="lfx-error" role="alert" data-testid="reminders-dialog-error">
            {limitAwareMessage(create.error, 'Du hast die Höchstzahl an Erinnerungen erreicht. Lösche oder erledige ältere Erinnerungen.')}
          </div>
        )}
      </form>
    </Dialog>
  );
}
